import { useMemo } from "react"
import { useLaborMixYear } from "../../shared/useLaborMixYear"
import {
  LABOR_CATEGORY_COLORS,
  type LaborCategoryKey,
} from "../../shared/laborCategories"
import {
  getEconomicValueDatumForYear,
  type LaborMixRegion,
  type RegionalShares,
} from "../../shared/laborEconomicValueData"
import { deriveAgentDataForYear } from "../../shared/computeDerivedAgents"
import { getLaborDatumForYear } from "../../shared/getLaborDatumForYear"
import { useHudContext } from "../../../hud/shared/playbookHUDInfra"
import { formatCompactUpper } from "../../../plan-a/economic-data/calculations"

// One bar in the sideways economic-value chart, including the per-region
// breakdown shown in the hover tooltip. `value` is the world-level $ figure
// that drives the bar's length.
export type LaborEconomicValueSegment = {
  key: LaborCategoryKey | "humans"
  // Pre-formatted display label, e.g. "3.5B Human Jobs", "220K Automated Coders, 41x speed".
  label: string
  // Raw count of the noun (jobs / AI copies / robots), so consumers don't have
  // to regex-parse `label` back into a number.
  count: number
  // Noun phrase from `label` without the leading count or trailing speed
  // qualifier, e.g. "Human Jobs", "Robots", "Top-Expert-Dominating AIs".
  unit: string
  // For AI segments, the integer speed multiplier ("7" for "7x speed") of the
  // typical (general-hardware) copy. Undefined when absent.
  speedMultiplier?: number
  // The fastest copies run: the inference-specialized fleet's multiplier, for
  // "at up to ×N speed" phrasings. Falls back to speedMultiplier when there
  // is no specialized fleet (branch plans; post-2040, where the sheet retires
  // the split).
  speedUpToMultiplier?: number
  value: number
  color: string
  filter?: string
  regionalValues: RegionalShares
}

type ColorOverrides = Partial<Record<LaborCategoryKey, string>>
type FilterOverrides = Partial<Record<LaborCategoryKey, string>>

type UseLaborEconomicValueSegmentsOptions = {
  year?: number
  combineHuman?: boolean
  colorOverrides?: ColorOverrides
  filterOverrides?: FilterOverrides
}

// Build the four bars (humansCog, humansPhys, aiCog, robotPhys) using
// Output Y × income-share. Labels are formatted in a separate helper because
// the AI label needs threshold/speed data from the Plan A ground truth.
export const useLaborEconomicValueSegments = ({
  year,
  combineHuman = false,
  colorOverrides,
  filterOverrides,
}: UseLaborEconomicValueSegmentsOptions = {}): {
  year: number
  segments: LaborEconomicValueSegment[]
} => {
  const resolvedYear = useLaborMixYear(year)
  // Branch plans (B/C/D/S) carry their own world compute and (employment-
  // collapsed) human worker counts on the phase; the derived agent count/
  // speed and the human side of the strip then follow the branch story.
  const currentPhase = useHudContext().currentPhase
  const branchHumansCog = currentPhase?.humanCogWorkers
  const branchHumansPhys = currentPhase?.humanPhysWorkers
  const branchCopies = currentPhase?.aiCopies
  const branchSpeed = currentPhase?.aiSpeedTokS
  return useMemo(() => {
    const datum = getEconomicValueDatumForYear(resolvedYear)
    const agent = deriveAgentDataForYear(resolvedYear)
    const laborDatum = getLaborDatumForYear(resolvedYear)
    // Branch phases carry their own copies count (econ model, balloons over
    // 2030-2032) and a single per-copy speed — no general/specialized split,
    // so Plan A's specialized fleet is dropped there. The noun still tracks
    // Plan A ground-truth capability. Plan A uses the spreadsheet series
    // directly.
    const onBranch = branchCopies !== undefined || branchSpeed !== undefined
    const ai = describeAi({
      ...agent,
      copies: branchCopies ?? agent.copies,
      speed: branchSpeed ?? agent.speed,
      copiesSpecialized: onBranch ? 0 : agent.copiesSpecialized,
      speedSpecialized: onBranch ? null : agent.speedSpecialized,
    })
    const robots = robotCount(resolvedYear)
    const humansCog = branchHumansCog ?? laborDatum.humansCog
    const humansPhys = branchHumansPhys ?? laborDatum.humansPhys
    // On branches the Plan A economic-value shares don't apply, so the dot
    // strip goes population-proportional instead: each worker (human or AI
    // copy) is worth the same fixed value, making the filled/hollow dot split
    // track the head counts (~100x more AI dots once copies reach the 100Bs).
    const isBranch = branchHumansCog !== undefined
    const fourSegments: LaborEconomicValueSegment[] = [
      buildSegment({
        key: "humansCog",
        count: humansCog,
        unit: "Human Cognitive Jobs",
        outputY: datum.outputY,
        share: datum.humansCogShare,
        valueOverride: isBranch ? humansCog * BRANCH_VALUE_PER_WORKER : undefined,
        colorOverrides,
        filterOverrides,
      }),
      buildSegment({
        key: "humansPhys",
        count: humansPhys,
        unit: "Human Physical Jobs",
        outputY: datum.outputY,
        share: datum.humansPhysShare,
        valueOverride: isBranch ? humansPhys * BRANCH_VALUE_PER_WORKER : undefined,
        colorOverrides,
        filterOverrides,
      }),
      buildSegment({
        key: "aiCog",
        count: ai.count,
        unit: ai.unit,
        speedMultiplier: ai.speedMultiplier,
        speedUpToMultiplier: ai.speedUpToMultiplier,
        outputY: datum.outputY,
        share: datum.aiCogShare,
        valueOverride: isBranch ? ai.count * BRANCH_VALUE_PER_WORKER : undefined,
        colorOverrides,
        filterOverrides,
      }),
      buildSegment({
        key: "robotPhys",
        count: robots,
        unit: "Robots",
        outputY: datum.outputY,
        share: datum.robotPhysShare,
        colorOverrides,
        filterOverrides,
      }),
    ]
    const segments = combineHuman
      ? mergeHumanSegments(fourSegments, humansCog + humansPhys)
      : fourSegments
    return { year: resolvedYear, segments }
  }, [resolvedYear, branchHumansCog, branchHumansPhys, branchCopies, branchSpeed, combineHuman, colorOverrides, filterOverrides])
}

const mergeHumanSegments = (
  segments: LaborEconomicValueSegment[],
  totalHumanJobs: number,
): LaborEconomicValueSegment[] => {
  const humansCog = segments.find((s) => s.key === "humansCog")
  const humansPhys = segments.find((s) => s.key === "humansPhys")
  if (!humansCog || !humansPhys) return segments
  const unit = "Human Jobs"
  const merged: LaborEconomicValueSegment = {
    key: "humans",
    label: `${formatCompactUpper(totalHumanJobs)} ${unit}`,
    count: totalHumanJobs,
    unit,
    color: humansCog.color,
    filter: humansCog.filter,
    value: humansCog.value + humansPhys.value,
    regionalValues: {
      us: humansCog.regionalValues.us + humansPhys.regionalValues.us,
      china: humansCog.regionalValues.china + humansPhys.regionalValues.china,
      row: humansCog.regionalValues.row + humansPhys.regionalValues.row,
      world: humansCog.regionalValues.world + humansPhys.regionalValues.world,
    },
  }
  return [merged, ...segments.filter((s) => s.key !== "humansCog" && s.key !== "humansPhys")]
}

// Flat per-worker value used for branch-plan segments (~world output per
// worker today). Only the RATIO matters — the dot grid normalizes by
// dollars-per-dot — but the magnitude keeps the strip continuous with Plan
// A's real $ values at the 2029 branch point.
const BRANCH_VALUE_PER_WORKER = 40_000

const buildSegment = ({
  key,
  count,
  unit,
  speedMultiplier,
  speedUpToMultiplier,
  outputY,
  share,
  valueOverride,
  colorOverrides,
  filterOverrides,
}: {
  key: LaborCategoryKey
  count: number
  unit: string
  speedMultiplier?: number
  speedUpToMultiplier?: number
  outputY: RegionalShares
  share: RegionalShares
  // Branch plans replace the Plan A $ value (outputY x share) with a
  // population-proportional one; regional splits keep Plan A's proportions.
  valueOverride?: number
  colorOverrides?: ColorOverrides
  filterOverrides?: FilterOverrides
}): LaborEconomicValueSegment => {
  const baseValues: RegionalShares = {
    us: outputY.us * share.us,
    china: outputY.china * share.china,
    row: outputY.row * share.row,
    world: outputY.world * share.world,
  }
  const regionalValues: RegionalShares = valueOverride === undefined
    ? baseValues
    : {
      us: baseValues.world > 0 ? (baseValues.us / baseValues.world) * valueOverride : valueOverride * 0.6,
      china: baseValues.world > 0 ? (baseValues.china / baseValues.world) * valueOverride : valueOverride * 0.15,
      row: baseValues.world > 0 ? (baseValues.row / baseValues.world) * valueOverride : valueOverride * 0.25,
      world: valueOverride,
    }
  const speedSuffix = speedMultiplier ? `, ${formatCompactUpper(speedMultiplier)}x speed` : ""
  return {
    key,
    label: count > 0 ? `${formatCompactUpper(count)} ${unit}${speedSuffix}` : unit,
    count,
    unit,
    speedMultiplier,
    speedUpToMultiplier,
    value: regionalValues.world,
    color: colorOverrides?.[key] ?? LABOR_CATEGORY_COLORS[key],
    filter: filterOverrides?.[key],
    regionalValues,
  }
}

// Robot count from row 37 (Plan A, World, Robot count). Raw units, not HE.
const ROBOT_COUNT_BY_YEAR: Record<number, number> = {
  2025: 1.0e7, 2026: 1.16e7, 2027: 1.33e7, 2028: 1.53e7, 2029: 1.82e7,
  2030: 2.3e7, 2031: 3.57e7, 2032: 7.2e7, 2033: 1.7e8, 2034: 4.23e8,
  2035: 1.0e9, 2036: 2.26e9, 2037: 5.28e9, 2038: 1.25e10, 2039: 2.97e10, 2040: 7.0e10,
}

const robotCount = (year: number): number => {
  const exact = ROBOT_COUNT_BY_YEAR[year]
  if (exact !== undefined) return exact
  const years = Object.keys(ROBOT_COUNT_BY_YEAR).map(Number).sort((a, b) => a - b)
  const clamped = Math.max(years[0], Math.min(years[years.length - 1], year))
  return ROBOT_COUNT_BY_YEAR[clamped] ?? ROBOT_COUNT_BY_YEAR[years[0]]
}

// Build the raw count / unit / speed for the AI segment from the derived agent
// data. `unit` is the noun alone ("Top-Expert-Dominating AIs"); the speed
// qualifiers (when present) live on `speedMultiplier` (typical copy) and
// `speedUpToMultiplier` (inference-specialized copies) and consumers reattach
// them as needed. `count` covers the whole fleet, both hardware tiers.
export const roundToSignificantFigures = (value: number, sigFigs: number): number => {
  if (value === 0) return 0
  const magnitude = Math.pow(10, sigFigs - Math.ceil(Math.log10(Math.abs(value))))
  return Math.round(value * magnitude) / magnitude
}

const describeAi = (agent: ReturnType<typeof deriveAgentDataForYear>): {
  count: number, unit: string, speedMultiplier?: number, speedUpToMultiplier?: number,
} => {
  const count = agent.copies + agent.copiesSpecialized
  if (count <= 0) return { count: 0, unit: "AI" }
  const asMultiplier = (tokS: number): number | undefined => {
    const speedHuman = tokS / 10 // existing convention: human = 10 tokens/s
    // 2 significant figures: the speed is a forecast, and a multiplier like
    // ×1,937 implies precision the model doesn't have.
    return speedHuman >= 1
      ? roundToSignificantFigures(Math.round(speedHuman), 2)
      : undefined
  }
  const speedMultiplier = asMultiplier(agent.speed)
  const speedUpToMultiplier = agent.speedSpecialized === null
    ? speedMultiplier
    : asMultiplier(agent.speedSpecialized)
  return { count, unit: `${agent.thresholdLabel}s`, speedMultiplier, speedUpToMultiplier }
}

export const formatRegionalValueLabel = (region: LaborMixRegion): string => {
  switch (region) {
    case "us":
      return "US"
    case "china":
      return "China"
    case "row":
      return "RoW"
    case "world":
      return "Total"
  }
}

export const formatCurrencyCompact = (value: number): string => {
  if (!isFinite(value) || value <= 0) return "$0"
  return `$${formatCompactUpper(value)}`
}
